import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { assignmentRulesAPI, pipelineAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineSwitchHorizontal, HiOutlinePlus, HiOutlineTrash,
  HiOutlinePencil, HiOutlineArrowUp, HiOutlineArrowDown,
  HiOutlineCheck, HiOutlineX
} from 'react-icons/hi';
import { formatLabel } from '../../utils/formatLabel';

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'low', label: 'Low', color: 'bg-green-100 text-green-700' },
];

const SOURCE_OPTIONS = [
  'Website', 'Referral', 'Cold Call', 'LinkedIn', 'Email Campaign',
  'Social Media', 'Advertisement', 'Walk-in', 'Phone Inquiry', 'Other'
];

// Create/Edit Rule Modal
const RuleModal = ({ isOpen, onClose, rule, users, products, pipelineStages, onSave }) => {
  const isEdit = !!rule;
  const [formData, setFormData] = useState({
    name: rule?.name || '',
    description: rule?.description || '',
    isActive: rule?.isActive !== undefined ? rule.isActive : true,
    priority: rule?.priority || 0,
    conditions: {
      sources: rule?.conditions?.sources || [],
      priorities: rule?.conditions?.priorities || [],
      stages: rule?.conditions?.stages || [],
      productIds: rule?.conditions?.productIds?.map(id => id._id || id) || [],
      tags: rule?.conditions?.tags || [],
    },
    strategy: rule?.strategy || 'round_robin',
    assignToUserIds: rule?.assignToUserIds?.map(id => id._id || id) || [],
    maxLeadsPerAgent: rule?.maxLeadsPerAgent || '',
    fallbackUserId: rule?.fallbackUserId?._id || rule?.fallbackUserId || '',
  });
  const [formError, setFormError] = useState('');
  const [tagInput, setTagInput] = useState('');

  // Filter users to only telecallers and managers
  // Already-selected users stay visible even if deactivated, so they can be removed
  const eligibleUsers = users.filter(u =>
    (u.role === 'telecaller' || u.role === 'manager' || u.role === 'admin') &&
    (u.isActive !== false ||
      formData.assignToUserIds.includes(u._id) ||
      formData.fallbackUserId === u._id)
  );

  const toggleArrayItem = (field, value) => {
    setFormData(prev => {
      const arr = prev.conditions[field];
      return {
        ...prev,
        conditions: {
          ...prev.conditions,
          [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]
        }
      };
    });
  };

  const toggleUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      assignToUserIds: prev.assignToUserIds.includes(userId)
        ? prev.assignToUserIds.filter(id => id !== userId)
        : [...prev.assignToUserIds, userId]
    }));
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.conditions.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        conditions: { ...prev.conditions, tags: [...prev.conditions.tags, tag] }
      }));
    }
    setTagInput('');
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      conditions: { ...prev.conditions, tags: prev.conditions.tags.filter(t => t !== tag) }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setFormError('Rule name is required');
      return;
    }
    if (formData.assignToUserIds.length === 0 && !formData.fallbackUserId) {
      setFormError('Select at least one agent or a fallback user');
      return;
    }
    setFormError('');
    // Clean up: convert empty maxLeadsPerAgent to null
    const data = {
      ...formData,
      maxLeadsPerAgent: formData.maxLeadsPerAgent ? parseInt(formData.maxLeadsPerAgent, 10) || null : null,
    };
    onSave(data);
  };

  // Close on Escape, like the shared Modal
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="assignment-rule-modal-title"
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <h2 id="assignment-rule-modal-title" className="text-base font-semibold text-gray-900">
            {isEdit ? 'Edit Assignment Rule' : 'Create Assignment Rule'}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            New leads matching these conditions will be auto-assigned to agents via round-robin.
          </p>
        </div>

        {/* Scrollable form body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">
            {/* Name + Priority row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rule Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="e.g., Website leads round-robin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority <span className="text-gray-400 font-normal">(lower = runs first)</span>
                </label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  min={0}
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
                rows={2}
                placeholder="When should this rule apply?"
              />
            </div>

            {/* Conditions Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Conditions <span className="text-gray-400 font-normal">(a lead must match ALL filled conditions — leave empty to match all)</span>
                </label>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden space-y-0">
                {/* Source */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Source</p>
                  <div className="flex flex-wrap gap-1.5">
                    {SOURCE_OPTIONS.map(src => {
                      const selected = formData.conditions.sources.includes(src);
                      return (
                        <button
                          key={src}
                          type="button"
                          onClick={() => toggleArrayItem('sources', src)}
                          className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                            selected ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          {src}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Priority */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Lead Priority</p>
                  <div className="flex flex-wrap gap-1.5">
                    {PRIORITY_OPTIONS.map(p => {
                      const selected = formData.conditions.priorities.includes(p.value);
                      return (
                        <button
                          key={p.value}
                          type="button"
                          onClick={() => toggleArrayItem('priorities', p.value)}
                          className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                            selected ? p.color : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Stage */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Pipeline Stage</p>
                  <div className="flex flex-wrap gap-1.5">
                    {pipelineStages.map(stage => {
                      const selected = formData.conditions.stages.includes(stage.key);
                      return (
                        <button
                          key={stage.key}
                          type="button"
                          onClick={() => toggleArrayItem('stages', stage.key)}
                          className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                            selected ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          {stage.name}
                        </button>
                      );
                    })}
                    {pipelineStages.length === 0 && (
                      <span className="text-xs text-gray-400">No stages found</span>
                    )}
                  </div>
                </div>

                {/* Tags */}
                <div className="px-4 py-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Tags</p>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {formData.conditions.tags.map(tag => (
                      <span key={tag} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)} className="hover:text-blue-900">
                          <HiOutlineX className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      placeholder="Add a tag and press Enter"
                    />
                    <button type="button" onClick={addTag} className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Agent Pool */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Agent Pool <span className="text-red-500">*</span>
                <span className="text-gray-400 font-normal ml-1">(agents will receive leads via round-robin)</span>
              </label>
              {eligibleUsers.length === 0 ? (
                <p className="text-sm text-gray-400">No telecallers or managers found. Add users first.</p>
              ) : (
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                  {eligibleUsers.map(user => {
                    const selected = formData.assignToUserIds.includes(user._id);
                    return (
                      <label
                        key={user._id}
                        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${
                          selected ? 'bg-primary-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleUser(user._id)}
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <div className="min-w-0">
                          <p className={`text-sm ${selected ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs text-gray-500">{user.email} · {formatLabel(user.role)}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Max Leads Per Agent */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Leads Per Agent <span className="text-gray-400 font-normal">(leave empty for unlimited)</span>
              </label>
              <input
                type="number"
                min="1"
                value={formData.maxLeadsPerAgent}
                onChange={(e) => setFormData(prev => ({ ...prev, maxLeadsPerAgent: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                placeholder="e.g., 50 — agents won't get more leads once they hit this limit"
              />
              <p className="text-xs text-gray-400 mt-1">
                When an agent reaches this many active leads, they are skipped and the next agent is tried. If all agents are at max, the lead goes to the fallback user.
              </p>
            </div>

            {/* Fallback User */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fallback User <span className="text-gray-400 font-normal">(used only when all agents are at max capacity or pool is empty — not for leads that don't match)</span>
              </label>
              <select
                value={formData.fallbackUserId}
                onChange={(e) => setFormData(prev => ({ ...prev, fallbackUserId: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              >
                <option value="">No fallback</option>
                {eligibleUsers.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName} ({formatLabel(user.role)})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Error */}
          {formError && (
            <div className="px-6 pb-3">
              <p className="text-sm text-red-600 font-medium bg-red-50 px-3 py-2 rounded-md">{formError}</p>
            </div>
          )}

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors shadow-sm"
            >
              {isEdit ? 'Update Rule' : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Component
const AssignmentRules = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleteRule, setDeleteRule] = useState(null);

  // Fetch auto-assign toggle status
  const { data: autoAssignData } = useQuery({
    queryKey: ['autoAssign'],
    queryFn: async () => {
      const response = await assignmentRulesAPI.getAutoAssign();
      return response.data;
    }
  });

  const autoAssignEnabled = autoAssignData?.data?.enabled || false;

  // Rules / auto-assign also pick up leads that were already unassigned —
  // the backend returns how many it assigned as `backfill`.
  const notifyBackfill = (backfill) => {
    if (!backfill || !backfill.processed) return;
    queryClient.invalidateQueries(['leads']);
    if (backfill.assigned > 0) {
      toast.success(`${backfill.assigned} existing unassigned lead${backfill.assigned === 1 ? '' : 's'} assigned`);
    }
    if (backfill.remaining > 0) {
      toast(`${backfill.remaining} lead${backfill.remaining === 1 ? '' : 's'} still unassigned (no rule matched)`, { icon: 'ℹ️' });
    }
  };

  // Toggle auto-assign mutation
  const autoAssignMutation = useMutation({
    mutationFn: (enabled) => assignmentRulesAPI.toggleAutoAssign(enabled),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['autoAssign']);
      toast.success(autoAssignEnabled ? 'Auto-assign disabled' : 'Auto-assign enabled');
      notifyBackfill(res.data?.backfill);
    },
    onError: (error) => toast.error(error.response?.data?.message || 'Failed to toggle auto-assign')
  });

  // Fetch rules
  const { data: rulesData, isLoading } = useQuery({
    queryKey: ['assignmentRules'],
    queryFn: async () => {
      const response = await assignmentRulesAPI.list();
      return response.data;
    }
  });

  // Fetch users (for agent pool)
  const { data: usersData } = useQuery({
    queryKey: ['users', 'assignmentRules'],
    queryFn: async () => {
      // /users is paginated (default 20) — fetch everyone for the agent pool
      const res = await api.get('/users', { params: { limit: 1000 } });
      return res.data?.data || res.data || [];
    }
  });

  // Fetch pipeline stages
  const { data: stagesData } = useQuery({
    queryKey: ['pipelineStages'],
    queryFn: async () => {
      const response = await pipelineAPI.getAll();
      return response.data?.data || response.data || [];
    }
  });

  // Fetch products
  const { data: productsData } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const res = await api.get('/products');
      return res.data?.data || res.data || [];
    }
  });

  const rules = rulesData?.data || [];
  const users = usersData || [];
  const pipelineStages = stagesData || [];
  const products = productsData || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => assignmentRulesAPI.create(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['assignmentRules']);
      setShowModal(false);
      toast.success('Assignment rule created');
      notifyBackfill(res.data?.backfill);
    },
    onError: (error) => toast.error(error.response?.data?.message || 'Failed to create rule')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => assignmentRulesAPI.update(id, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['assignmentRules']);
      setEditingRule(null);
      toast.success('Rule updated');
      notifyBackfill(res.data?.backfill);
    },
    onError: (error) => toast.error(error.response?.data?.message || 'Failed to update rule')
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => assignmentRulesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['assignmentRules']);
      setDeleteRule(null);
      toast.success('Rule deleted');
    },
    onError: (error) => toast.error(error.response?.data?.message || 'Failed to delete rule')
  });

  const toggleMutation = useMutation({
    mutationFn: (id) => assignmentRulesAPI.toggle(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['assignmentRules']);
      toast.success('Rule toggled');
      notifyBackfill(res.data?.backfill);
    },
    onError: (error) => toast.error(error.response?.data?.message || 'Failed to toggle rule')
  });

  const applyUnassignedMutation = useMutation({
    mutationFn: () => assignmentRulesAPI.applyToUnassigned(),
    onSuccess: (res) => {
      const result = res.data?.data;
      queryClient.invalidateQueries(['assignmentRules']);
      if (!result?.processed) {
        toast.success('No unassigned leads to assign');
        return;
      }
      notifyBackfill(result);
      if (!result.assigned) toast.error('No leads matched your rules — check the rule conditions');
    },
    onError: (error) => toast.error(error.response?.data?.message || 'Failed to assign unassigned leads')
  });

  const reorderMutation = useMutation({
    mutationFn: (order) => assignmentRulesAPI.reorder(order),
    onSuccess: () => {
      queryClient.invalidateQueries(['assignmentRules']);
    },
    onError: (error) => toast.error(error.response?.data?.message || 'Failed to reorder rules')
  });

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newOrder = [...rules];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    reorderMutation.mutate(newOrder.map(r => r._id));
  };

  const handleMoveDown = (index) => {
    if (index === rules.length - 1) return;
    const newOrder = [...rules];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    reorderMutation.mutate(newOrder.map(r => r._id));
  };

  // Rules come back with users populated — use that name first so chips
  // don't show "Unknown" while the users list is still loading
  const getUserName = (ref) => {
    if (!ref) return 'Unknown';
    const id = ref._id || ref;
    const user = (ref.firstName || ref.lastName) ? ref : users.find(u => u._id === id);
    return user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Unknown';
  };

  const getStageNames = (keys) => {
    return keys.map(key => {
      const stage = pipelineStages.find(s => s.key === key);
      return stage ? stage.name : key;
    });
  };

  return (
    <div>
      {/* Auto-Assign Toggle — Simple round-robin across all agents */}
      <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-5 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <HiOutlineSwitchHorizontal className="w-5 h-5 text-primary-600" />
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-gray-900">
                  Auto-Assign to All Agents
                </h4>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  When enabled, leads that don't match any rule below are distributed to ALL active telecallers and managers using round-robin. Turn this off if only the agents in your rules should get leads.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className={`text-xs font-semibold uppercase tracking-wide ${autoAssignEnabled ? 'text-primary-600' : 'text-gray-400'}`}>
                {autoAssignEnabled ? 'ON' : 'OFF'}
              </span>
              <button
                type="button"
                onClick={() => autoAssignMutation.mutate(!autoAssignEnabled)}
                disabled={autoAssignMutation.isPending}
                className={`
                  relative inline-flex flex-shrink-0 h-7 w-[52px] items-center justify-start
                  rounded-full overflow-hidden p-1 box-border
                  min-h-0 min-w-0
                  transition-colors duration-200 ease-in-out
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                  ${autoAssignEnabled ? 'bg-primary-600' : 'bg-gray-300'}
                  ${autoAssignMutation.isPending ? 'opacity-60 cursor-wait' : 'cursor-pointer'}
                `}
                role="switch"
                aria-checked={autoAssignEnabled}
                title={autoAssignEnabled ? 'Disable auto-assign' : 'Enable auto-assign'}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg ring-0
                    transition-transform duration-200 ease-in-out
                    ${autoAssignEnabled ? 'translate-x-[24px]' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>
          </div>
        </div>
        {autoAssignEnabled && (
          <div className="px-5 py-2.5 bg-green-50 border-t border-green-100 flex items-center gap-2">
            <HiOutlineCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="text-xs text-green-700">
              <span className="font-medium">Active</span> — Every new lead (without manual assignment) will be round-robin distributed to all active telecallers and managers.
            </p>
          </div>
        )}
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <HiOutlineSwitchHorizontal className="w-4 h-4 text-primary-600 flex-shrink-0" />
            Lead Assignment Rules
          </h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Automatically assign new leads to agents using round-robin rules.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => applyUnassignedMutation.mutate()}
            disabled={applyUnassignedMutation.isPending}
            title="Run the active rules on leads that are currently unassigned"
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-wait"
          >
            <HiOutlineSwitchHorizontal className="w-4 h-4" />
            {applyUnassignedMutation.isPending ? 'Assigning...' : 'Assign Unassigned Leads'}
          </button>
          <button
            onClick={() => { setEditingRule(null); setShowModal(true); }}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors shadow-sm whitespace-nowrap"
          >
            <HiOutlinePlus className="w-4 h-4" />
            Add Rule
          </button>
        </div>
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex items-center justify-center py-10 text-gray-400">
          <svg className="animate-spin w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading rules...
        </div>
      ) : rules.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-200">
          <HiOutlineSwitchHorizontal className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-500">No assignment rules yet</p>
          <p className="text-xs text-gray-400 mt-1">Create a rule to automatically assign new leads to agents.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map((rule, index) => (
            <div key={rule._id} className={`border rounded-lg overflow-hidden transition-colors ${
              rule.isActive ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-75'
            }`}>
              {/* Card header */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex flex-col gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      title="Move up"
                    >
                      <HiOutlineArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleMoveDown(index)}
                      disabled={index === rules.length - 1}
                      className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      title="Move down"
                    >
                      <HiOutlineArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h5 className="text-sm font-semibold text-gray-900 truncate">{rule.name}</h5>
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-500">
                        #{rule.priority}
                      </span>
                      {!rule.isActive && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">
                          Disabled
                        </span>
                      )}
                    </div>
                    {rule.description && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{rule.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => toggleMutation.mutate(rule._id)}
                    className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${
                      rule.isActive
                        ? 'text-yellow-700 bg-yellow-50 border border-yellow-200 hover:bg-yellow-100'
                        : 'text-green-700 bg-green-50 border border-green-200 hover:bg-green-100'
                    }`}
                    disabled={toggleMutation.isPending}
                  >
                    {rule.isActive ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => setEditingRule(rule)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 rounded transition-colors"
                    title="Edit rule"
                  >
                    <HiOutlinePencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteRule(rule)}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                    title="Delete rule"
                  >
                    <HiOutlineTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Card body */}
              <div className="px-4 py-3 space-y-2">
                {/* Conditions */}
                <div>
                  <p className="text-xs text-gray-500 mb-1">Conditions:</p>
                  {(
                    !rule.conditions?.sources?.length &&
                    !rule.conditions?.priorities?.length &&
                    !rule.conditions?.stages?.length &&
                    !rule.conditions?.productIds?.length &&
                    !rule.conditions?.tags?.length
                  ) ? (
                    <span className="text-xs text-gray-400 italic">Matches all leads</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {rule.conditions?.sources?.map(s => (
                        <span key={s} className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">{s}</span>
                      ))}
                      {rule.conditions?.priorities?.map(p => {
                        const opt = PRIORITY_OPTIONS.find(o => o.value === p);
                        return (
                          <span key={p} className={`text-xs px-2 py-0.5 rounded font-medium ${opt?.color || 'bg-gray-100 text-gray-600'}`}>
                            {opt?.label || p}
                          </span>
                        );
                      })}
                      {rule.conditions?.stages?.map(s => (
                        <span key={s} className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">
                          {getStageNames([s])[0]}
                        </span>
                      ))}
                      {rule.conditions?.tags?.map(t => (
                        <span key={t} className="text-xs px-2 py-0.5 rounded bg-cyan-100 text-cyan-700 font-medium">{t}</span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Agent pool */}
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    Agents (round-robin){rule.maxLeadsPerAgent ? ` · max ${rule.maxLeadsPerAgent} leads each` : ''}:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {(rule.assignToUserIds || []).map(ref => (
                      // Key by user id — names repeat ("Unknown" while loading) and
                      // duplicate keys left stale chips behind (3 agents showed as 5)
                      <span key={ref._id || ref} className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">
                        {getUserName(ref)}
                      </span>
                    ))}
                    {(rule.assignToUserIds || []).length === 0 && (
                      <span className="text-xs text-gray-400 italic">No agents selected</span>
                    )}
                  </div>
                </div>

                {/* Max leads & Fallback */}
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {rule.maxLeadsPerAgent && (
                    <p className="text-xs text-gray-500">
                      Max leads: <span className="font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">{rule.maxLeadsPerAgent} per agent</span>
                    </p>
                  )}
                  {rule.fallbackUserId && (
                    <p className="text-xs text-gray-500">
                      Fallback: <span className="font-medium text-gray-700">{getUserName(rule.fallbackUserId)}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteRule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <HiOutlineTrash className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-gray-900">Delete Rule?</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Delete <strong>{deleteRule.name}</strong>? New leads will no longer be auto-assigned by this rule.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button onClick={() => setDeleteRule(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button onClick={() => deleteMutation.mutate(deleteRule._id)} disabled={deleteMutation.isPending} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50">
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <RuleModal
        // Remount per rule — form state is initialised from `rule` only on mount,
        // so without a key Edit opened with an empty form
        key={editingRule?._id || (showModal ? 'new' : 'closed')}
        isOpen={showModal || !!editingRule}
        onClose={() => { setShowModal(false); setEditingRule(null); }}
        rule={editingRule}
        users={users}
        products={products}
        pipelineStages={pipelineStages}
        onSave={(data) => {
          if (editingRule) {
            updateMutation.mutate({ id: editingRule._id, data });
          } else {
            createMutation.mutate(data);
          }
        }}
      />
    </div>
  );
};

export default AssignmentRules;